import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Carousel,
  CarouselIndicators,
  CarouselItem,
  Col,
  FormGroup,
  FormText,
  Row,
} from 'reactstrap';
import { BadgeArtSelectorModel } from '../../../../shared/kiosk';
import { BadgeArt } from '../../../models';
import { isMobile } from '../../../services';
import { useTranslation } from '../../../translations';
import { Fetcher, isLogicError, useFetcher } from '../../../utils';
import { captureError, LogicError } from '../../../utils/errorHandling';
import { MaterialIcon } from '../../MaterialIcon';

interface BadgeArtRowProps extends BadgeArtSelectorModel {
  readonly registrationId?: number;
  readonly badgeArtId: number | null;
  readonly defaultBadgeArtId: number | null;
  readonly userId?: number;
  readonly setBadgeArtId: (value: number) => void;
}

export const FormNodeBadgeArtSelector: FC<BadgeArtRowProps> = ({
  registrationId,
  setBadgeArtId,
  badgeArtId,
  defaultBadgeArtId,
  userId,
}) => {
  const badgeArts = useFetcher(async () => {
    return await api.getBadgeArt();
  }, []);

  useEffect(() => {
    if (badgeArtId || !badgeArts.data?.length) {
      return;
    }

    const randomArts = badgeArts.data.filter((t) => t.limit === null || t.count < t.limit);
    if (randomArts.length > 0) {
      // Get random art
      setBadgeArtId(randomArts[Math.floor(Math.random() * randomArts.length)]!.id);
    }
  }, [badgeArtId, setBadgeArtId, badgeArts.data]);

  const art: BadgeArt[] = (badgeArts.data ?? []).filter((t) => {
    if (t.limit === null) {
      return true;
    }

    if (defaultBadgeArtId === t.id) {
      return true;
    }

    return t.count < t.limit;
  });

  return (
    <FormGroup>
      <Fetcher inline result={badgeArts}>
        <BadgeArtSelector
          art={art.map((t) => t.id)}
          artId={badgeArtId}
          onChange={setBadgeArtId}
          refresh={badgeArts.refresh}
          registrationId={registrationId}
          userId={userId}
        />
      </Fetcher>
    </FormGroup>
  );
};

interface BadgeArtSelectorProps {
  readonly art: number[];
  readonly artId: number | null;
  readonly registrationId?: number;
  readonly userId?: number;
  readonly onChange: (artId: number) => void;
  readonly refresh: () => void;
}

const MobileBadgeArtSelector: FC<{
  readonly art: number[];
  readonly currentArtIndex: number;
  readonly setAnimating: (animating: boolean) => void;
  readonly goToIndex: (index: number) => void;
}> = ({ art, currentArtIndex, setAnimating, goToIndex }) => {
  const items = art.map((id) => ({
    altText: `Badge ${id}`,
    src: `/api/badgeart/${id}/image`,
  }));

  const slides = items.map((item) => (
    <CarouselItem
      key={item.src}
      onExited={() => {
        setAnimating(false);
      }}
      onExiting={() => {
        setAnimating(true);
      }}
    >
      <img alt={item.altText} className="img-fluid" src={item.src} />
    </CarouselItem>
  ));

  const moveIndex = useCallback(
    (indexMod: number) => {
      goToIndex((currentArtIndex + indexMod) % art.length);
    },
    [goToIndex, currentArtIndex, art],
  );

  const { ts } = useTranslation();

  return (
    <Carousel
      activeIndex={currentArtIndex}
      interval={false}
      next={() => {
        moveIndex(1);
      }}
      previous={() => {
        moveIndex(-1);
      }}
    >
      <CarouselIndicators
        activeIndex={currentArtIndex}
        items={items}
        onClickHandler={(idx) => {
          goToIndex(idx);
        }}
      />
      {slides}
      <a
        className="carousel-control-prev"
        onClick={() => {
          moveIndex(-1);
        }}
      >
        <MaterialIcon large name="keyboard_arrow_left" />
        <span className="visually-hidden">{ts('previous')}</span>
      </a>
      <a
        className="carousel-control-next"
        onClick={() => {
          moveIndex(1);
        }}
      >
        <MaterialIcon large name="keyboard_arrow_right" />
        <span className="visually-hidden">{ts('next')}</span>
      </a>
    </Carousel>
  );
};

const BadgeArtSelector: FC<BadgeArtSelectorProps> = ({
  art,
  artId,
  registrationId,
  userId,
  onChange,
  refresh,
}) => {
  const [animating, setAnimating] = useState(false);
  const [loading, setLoading] = useState(false);

  const { ts } = useTranslation();

  const currentArtIndex = useMemo(() => {
    if (!artId || art.length === 0) {
      return 0;
    }

    const index = art.indexOf(artId);

    return index === -1 ? 0 : index;
  }, [art, artId]);

  const quickUpdateBadge = useCallback(
    async (index: number) => {
      if (registrationId === undefined || userId === undefined) {
        return;
      }

      setLoading(true);

      try {
        await api.upsertRegistration(userId, {
          badgeArtId: index,
        });
      } catch (error) {
        captureError(error as Error);
        if (isLogicError(error, LogicError.BadgeArtLimitReached)) {
          refresh();
        }
      }

      setLoading(false);
    },
    [registrationId, userId, setLoading, refresh],
  );

  const goToIndex = useCallback(
    (newIndex: number) => {
      const newArt = art[newIndex];
      if (animating || currentArtIndex === newIndex || !newArt) {
        return;
      }

      onChange(newArt);

      quickUpdateBadge(newArt).catch(captureError);
    },
    [art, animating, currentArtIndex, onChange, quickUpdateBadge],
  );

  if (art.length === 0) {
    return <FormText color="muted">{ts('there_is_currently_no_badge_art_to_choose')}</FormText>;
  }

  if (isMobile) {
    return (
      <MobileBadgeArtSelector
        art={art}
        currentArtIndex={currentArtIndex}
        goToIndex={goToIndex}
        setAnimating={setAnimating}
      />
    );
  }

  return (
    <Row className="justify-content-center">
      {art.map((id, index) => {
        const isSelected = artId === id;
        return (
          <Col key={id} style={{ marginBottom: '1em' }} xl={3} xs={4}>
            <img
              className="w-100"
              src={`/api/badgeart/${id}/image`}
              style={{ marginBottom: '10px' }}
            />
            {isSelected ? (
              <Button
                block
                className={loading ? 'saving' : 'selected'}
                color="secondary"
                disabled
                id={`badge${id}`}
                outline
              >
                {ts(loading ? 'common.saving' : 'common.selected')}
              </Button>
            ) : (
              <Button
                block
                color="primary"
                disabled={loading}
                id={`badge${id}`}
                onClick={() => {
                  goToIndex(index);
                }}
              >
                {ts('common.select')}
              </Button>
            )}
          </Col>
        );
      })}
    </Row>
  );
};
